/**
 * El reporte diario — la pantalla que un supervisor abre todos los días.
 *
 * Spanish that a crew would actually use, not a dictionary rendering. The decisions worth writing
 * down, because somebody will otherwise "fix" them back:
 *
 * ── LO QUE SE DEJÓ EN INGLÉS, A PROPÓSITO ────────────────────────────────────────────────────────
 *
 *   • `Field Tickets` (in `generatedFieldTickets`). This names a specific BILLING document, and this
 *     application carries two that are not the same thing — a field ticket (T&M work, billed through
 *     invoice_packages) and a service ticket. Spanish offers `boleta`, `vale` and `orden` for both,
 *     and every one of them collapses a distinction the office invoices on. Same call as
 *     `navServiceTickets` in ./timekeeping.js, for the same reason. An English label a foreman can
 *     read out to the office is not a failure; a guessed word that sends him to the wrong document is.
 *
 * ── LO QUE SE ARREGLÓ AL TRADUCIR ────────────────────────────────────────────────────────────────
 *
 *   • `NCNS` becomes `Faltó sin avisar`. The acronym is opaque in English too — it is a US payroll
 *     code — and the Spanish says exactly what it means with no guessing involved. The STORED value
 *     is still `no_call_no_show`, so payroll sees no difference at all.
 *   • `Job` becomes `Proyecto`, the same word as `Project`. The English calls the identical picker
 *     "Project" in three sections and "Job" in the fourth; the list is literally the same
 *     `sectionProjectOptions` memo. Carrying that into Spanish would have invented two things where
 *     the data has one, and a foreman would reasonably go looking for the difference.
 *   • `Verify` becomes `Verificar`, kept clear of `Revisar` — which is what a TIMEKEEPER does to a
 *     submitted report, a different act by a different person. See ./timekeeping.js `review`.
 *
 * ── LAS PALABRAS ─────────────────────────────────────────────────────────────────────────────────
 *
 *   `cuadrilla` = crew. `supervisor` = foreman (taken from the QR page, not re-decided). `nómina` =
 *   payroll. `obra` = the job site, used only where the English is talking about the physical job.
 *   `capturar` = to enter data, which is what a Mexican office calls it.
 *
 *   `formulario` here, where the QR page says `hoja`. Not an inconsistency: this screen sits inside
 *   the application, where the module in the nav strip is `Formularios`, and a foreman moves between
 *   the two. The QR page is read by a different population who never see that nav strip.
 */

/**
 * The work vocabulary the server seeds (in English) for every company — the weld family, its
 * pickers and their choices — by KEY, so a Spanish-reading foreman sees Spanish on the card he fills
 * in every day. A word a company added itself is not here and shows as the company wrote it.
 *
 * Sizes stay as the drawings write them (2", 1-1/2"), and so do the trade codes a welder reads in
 * any language: STD, XS, XXS, 6G, GTAW, SMAW. `Céd.` (cédula) is what the pipe trade in Mexico calls
 * a schedule; `sobrecabeza` is overhead.
 */
const VOCABULARIO = {
  weld: 'Soldadura',
  'weld.material': 'Material',
  'weld.material.carbon_steel': 'Acero al carbono',
  'weld.material.stainless_304': 'Inoxidable 304/304L',
  'weld.material.stainless_316': 'Inoxidable 316/316L',
  'weld.material.duplex': 'Inoxidable dúplex',
  'weld.material.chrome_moly': 'Cromo-molibdeno (P11/P22/P91)',
  'weld.material.alloy': 'Aleación (otra)',
  'weld.material.aluminum': 'Aluminio',
  'weld.material.copper_nickel': 'Cobre-níquel',
  'weld.size': 'Diámetro nominal',
  'weld.size.nps_over_48': 'Más de 48"',
  'weld.schedule': 'Cédula / espesor',
  'weld.schedule.sch_5s': 'Céd. 5S',
  'weld.schedule.sch_10s': 'Céd. 10S',
  'weld.schedule.sch_10': 'Céd. 10',
  'weld.schedule.sch_20': 'Céd. 20',
  'weld.schedule.sch_30': 'Céd. 30',
  'weld.schedule.sch_40s': 'Céd. 40S',
  'weld.schedule.sch_40': 'Céd. 40',
  'weld.schedule.sch_60': 'Céd. 60',
  'weld.schedule.sch_80s': 'Céd. 80S',
  'weld.schedule.sch_80': 'Céd. 80',
  'weld.schedule.sch_100': 'Céd. 100',
  'weld.schedule.sch_120': 'Céd. 120',
  'weld.schedule.sch_140': 'Céd. 140',
  'weld.schedule.sch_160': 'Céd. 160',
  'weld.position': 'Posición',
  'weld.position.1g': '1G (girando)',
  'weld.position.2g': '2G (horizontal)',
  'weld.position.5g': '5G (horizontal fija)',
  'weld.position.6g': '6G (fija a 45°)',
  'weld.position.6gr': '6GR (restringida)',
  'weld.position.1f': '1F (filete, girando)',
  'weld.position.2f': '2F (filete, vertical fija)',
  'weld.position.2fr': '2FR (filete, girando horizontal)',
  'weld.position.4f': '4F (filete, sobrecabeza)',
  'weld.position.5f': '5F (filete, horizontal fija)',
  'weld.joint': 'Tipo de junta',
  'weld.joint.butt': 'A tope',
  'weld.joint.socket': 'De caja (socket)',
  'weld.joint.fillet': 'Filete',
  'weld.joint.branch': 'Ramal / injerto',
  'weld.joint.olet': 'Olet',
  'weld.joint.flange': 'Brida',
  'weld.process': 'Proceso',
  'weld.process.smaw': 'SMAW (electrodo)',
  'weld.process.gtaw_smaw': 'Raíz GTAW / relleno SMAW',
  'weld.process.gtaw_fcaw': 'Raíz GTAW / relleno FCAW',
  'weld.access': 'Acceso',
  'weld.access.shop': 'Taller',
  'weld.access.field_open': 'Campo - abierto',
  'weld.access.field_restricted': 'Campo - restringido',
  'weld.access.field_overhead': 'Campo - sobrecabeza / confinado',
}

export default {
  loadingReport: 'Cargando el reporte…',
  title: 'Reporte diario',
  modeEdit: 'Editando',
  modeNew: 'Nuevo',
  enteredBy: (name) => `(capturado por ${name})`,

  // ── el encabezado ─────────────────────────────────────────────────────────────────────────────
  date: 'Fecha',
  dateEditableOnRejected: 'La fecha se puede cambiar en los reportes rechazados',
  dateEditableOnRejectedNote: '✏️ La fecha se puede cambiar en los reportes rechazados',
  present: 'Presentes',
  totalHours: 'Total de horas',
  projects: 'Proyectos',
  showAllProjects: 'Ver todos los proyectos',
  showAllProjectsHelp: 'Quita el filtro por departamento para ver todos los proyectos activos',

  // ── capturar el reporte de otra persona ───────────────────────────────────────────────────────
  creatingFor: 'Capturando para:',
  me: (name) => `${name} (yo)`,
  selectForeman: 'Escoja al supervisor…',
  onBehalfOf: 'En nombre de',
  foreman: 'Supervisor',

  // ── verificación ──────────────────────────────────────────────────────────────────────────────
  verificationResults: 'Resultado de la verificación',
  completeRequiredForms: 'Llene estos formularios obligatorios y vuelva a enviar:',
  fillForm: (name) => `Llenar «${name}»`,
  form: 'formulario',
  allChecksPassed: 'Todo está bien — ya se puede enviar',
  cannotSubmit: (n) => (n === 1
    ? 'No se puede enviar — 1 problema arriba'
    : `No se puede enviar — ${n} problemas arriba`),

  // ── presente pero sin horas ───────────────────────────────────────────────────────────────────
  presentNoHours: (n) => (n === 1
    ? '1 persona está marcada como presente y sin horas'
    : `${n} personas están marcadas como presentes y sin horas`),
  presentNoHoursHelp: 'A ellos no les va a llegar nada a nómina. Escriba sus horas abajo, o confirme '
    + 'que así fue — la confirmación se guarda con el reporte.',
  workedNoHoursConfirmed: 'no trabajó horas — confirmado',
  workedNoHoursWhy: '¿Por qué? (lo mandaron a casa, de guardia, viajando…)',
  workedNoHoursReasonFor: (name) => `Motivo por el que ${name} no trabajó horas`,

  // ── formularios de este reporte ───────────────────────────────────────────────────────────────
  formsOnThisReport: 'Formularios de este reporte',
  backToDailyReport: 'Volver al reporte diario',

  // ── cuadrilla y horas ─────────────────────────────────────────────────────────────────────────
  crewAndHours: 'Cuadrilla y horas',
  hoursModeDetailed: 'Detallado',
  hoursModeDetailedHelp: 'Hora de entrada y salida por obra',
  hoursModeSplit: 'Horas repartidas',
  hoursModeSplitHelp: 'Horas por obra',
  hoursModeGrid: 'Cuadrícula',
  hoursModeGridHelp: 'Vista de hoja de cálculo',
  rollCall: (working, absent) => `Pase de lista — ${working} trabajando / ${absent} ausentes`,
  borrowedEmployee: 'Empleado prestado',
  pickEmployee: 'Escoja al empleado…',
  attendancePresent: 'Presente',
  attendanceAbsent: 'Faltó',
  attendanceLateArrival: 'Llegó tarde',
  attendanceEarlyOut: 'Salió temprano',
  attendanceBorrowed: 'Prestado',
  reasonPrompt: '¿motivo?',
  reasonSick: 'Enfermo',
  reasonVacation: 'Vacaciones',
  reasonPersonal: 'Personal',
  // `NCNS` spelled out — see the header. The stored value does not change.
  reasonNoCallNoShow: 'Faltó sin avisar',
  reasonInjury: 'Lesión',
  reasonWeather: 'Clima',
  reasonOther: 'Otro',
  reasonFreeText: 'Motivo…',
  absenceReasonIs: (word) => `Motivo: ${word}`,
  noHoursRecorded: 'Sin horas registradas',

  copyPreviousPrompt: 'Empiece más rápido copiando los datos de su reporte anterior',
  copyPreviousDay: '📋 Copiar el día anterior',
  importTimesheets: 'Importe hojas de horas desde Excel o PDF/escaneos',
  uploadFiles: 'Subir archivos',

  // ── las líneas ────────────────────────────────────────────────────────────────────────────────
  // Cuatro cadenas y no una plantilla: `Nuevo equipo` pero `Nueva descripción`. Ver el archivo en
  // inglés.
  newEquipment: 'Nuevo equipo',
  newMaterial: 'Nuevo material',
  newReceipt: 'Nuevo recibo',
  newWorkDescription: 'Nueva descripción del trabajo',
  numberedRow: (label, position) => `${label} ${position}`,
  equipment: 'Equipo',
  equipmentRow: 'Equipo',
  materials: 'Materiales',
  materialRow: 'Material',
  receipts: 'Recibos',
  receiptRow: 'Recibo',
  workDescriptions: 'Descripciones del trabajo',
  workDescriptionRow: 'Descripción del trabajo',
  project: 'Proyecto',
  selectProject: 'Escoja el proyecto…',
  selectEquipment: 'Escoja el equipo…',
  equipmentName: 'Nombre del equipo',
  customEntry: '— Otro (escribirlo) —',
  enterCustomEquipment: 'Escriba el nombre del equipo',
  unitNumber: 'No. de unidad',
  // `Unidad de tiempo` and not `Unidad`: the box beside it is `No. de unidad`, and two labels a
  // letter apart on one row is how a foreman puts the hours in the wrong box.
  unit: 'Unidad de tiempo',
  unitHour: 'Hora',
  unitDay: 'Día',
  unitWeek: 'Semana',
  // Las horas que la máquina estuvo en la obra, sea cual sea la unidad — la misma columna "Horas" del
  // ticket del cliente.
  equipmentHours: 'Horas',
  equipmentStandby: (pct) => `En espera (${pct}% de la tarifa)`,
  equipmentStandbyNotSet: 'En espera - su empresa ya no indica una tarifa de espera, así que esta línea no tendrá precio',
  unitNoLongerUsed: (unit) => `${unit} (ya no se usa)`,
  unitNoPriceHere: (unit) => `${unit} (sin precio en este trabajo)`,
  unitNotOffered: (unit) => `${unit} (no se ofrece para esta máquina)`,
  unitChargedByTheDay: (unit) => `${unit} (se cobra por día)`,
  hintNoPrice: 'Esta máquina no tiene precio en este trabajo - su línea quedará marcada',
  hintUnitsUnknown: 'No se pudo revisar en qué unidades se cobra esta máquina',
  hintGoverned: 'Se cobra según la opción semanal de su empresa',
  hintNeedsUnitNumber: 'Falta el No. de unidad: el cobro de esta máquina se cuenta por semana - use el mismo No. de unidad todos los días (¿no tiene número? escriba un nombre, por ejemplo Renta 1)',
  hintSplitNoUnitNumber: 'Sin No. de unidad esta máquina se cobra el día completo en este trabajo, sin repartirlo con sus otros trabajos de hoy',
  // QUÉ MÁQUINA (PLAN-30 P2): la máquina de Flota de ese tipo. Opcional - en blanco está bien, y el
  // cobro sigue por tipo de máquina. Solo nombres, nunca montos.
  whichMachine: '¿Qué máquina?',
  machineNotPicked: 'Sin escoger',
  machineNoneOfType: 'No hay máquinas de este tipo en Flota - déjelo en blanco',
  machineNotLinked: 'No está vinculada a una máquina de Flota. Elija la máquina en "¿Qué máquina?"; hasta entonces su costo en la obra no puede venir de la máquina.',
  machineNotLinkedTyped: (typed) => `No está vinculada a una máquina de Flota: ${typed} no es una de sus máquinas de este tipo. Elija la máquina en "¿Qué máquina?" y deje el No. de unidad como está: el cobro cuenta la máquina por él.`,
  machineListFailed: 'No se pudieron cargar las máquinas de este tipo - déjelo en blanco o intente de nuevo',
  // Máquinas rentadas (PLAN-30 P3).
  machineRentedNoCost: 'Rentada: su costo es la factura del proveedor, así que estas horas no llevan costo.',
  machineNewRental: '¿No aparece la máquina? Nueva renta',
  machineNewRentalTitle: 'Nueva renta',
  machineNoLongerOffered: (name) => `${name} (ya no se ofrece)`,
  machineOnReport: 'Máquina',
  machineStatus_out_of_service: 'fuera de servicio',
  machineStatus_under_repair: 'en reparación',
  machineStatus_inactive: 'inactiva',
  machineOwnership_leased: 'arrendada',
  machineOwnership_rented: 'rentada',
  machineOwnership_subcontracted: 'subcontratada',
  machineLeftBlank_sold: (n) => `La máquina ${n} está vendida, así que se dejó en blanco. Escoja otra o déjelo en blanco.`,
  machineLeftBlank_retired: (n) => `La máquina ${n} está retirada, así que se dejó en blanco. Escoja otra o déjelo en blanco.`,
  machineLeftBlank_deleted: (n) => `La máquina ${n} fue borrada de Flota, así que se dejó en blanco. Escoja otra o déjelo en blanco.`,
  machineLeftBlank_other_type: (n) => `La máquina ${n} es de otro tipo en Flota, así que se dejó en blanco. Escoja otra o déjelo en blanco.`,
  machineLeftBlank_type_changed: (n) => `El tipo de máquina cambió, así que la máquina ${n} se dejó en blanco. Escoja otra o déjelo en blanco.`,
  machineLeftBlank_not_yours: () => 'Esa máquina no es de las máquinas de Flota de su empresa, así que se dejó en blanco. Escoja otra o déjelo en blanco.',
  machineLeftBlank_no_type: () => 'Solo se puede escoger una máquina para un tipo de máquina de la lista, así que se dejó en blanco.',
  machineLeftBlank_fleet_unreadable: () => 'No se pudieron leer las máquinas de Flota, así que la máquina se dejó en blanco. Escójala de nuevo o déjelo en blanco.',
  machinesLeftBlank: (n) => (n === 1 ? '1 máquina se dejó en blanco - vea las filas de Equipo.'
    : `${n} máquinas se dejaron en blanco - vea las filas de Equipo.`),
  machineUnitNumberDiffers: (typed, num) => `El cobro usa la casilla No. de unidad (${typed || 'en blanco'}). ¿Usar ${num}?`,
  machineUnitNumberKept: (typed) => `El cobro usa la casilla No. de unidad (${typed || 'en blanco'}), el número que esta máquina ya tiene en esta obra esta semana: manténgalo.`,
  machineUnitNumberBilled: (typed) => `El cobro usa la casilla No. de unidad (${typed || 'en blanco'}).`,
  useMachineNumber: (num) => `Usar ${num}`,
  addEquipment: 'Agregar equipo',
  description: 'Descripción',
  materialDescription: 'Descripción del material',
  amount: 'Monto ($)',
  addMaterial: 'Agregar material',

  // ── capturar las horas: los tres modos de la sección Cuadrilla y horas ────────────────────────
  //
  // `Entrada` y `Salida` para clock-in / clock-out: es como se dice en una obra, y son las dos
  // palabras que un supervisor teclea cincuenta veces al día. `Comida` para el descanso de mediodía,
  // que es la palabra en México y Centroamérica.
  //
  // Las palabras de asistencia NO se repiten aquí — los tres modos usan `attendancePresent` … de
  // arriba, para que la palabra que decide si a alguien le pagan no cambie entre un modo y otro.
  disputed: 'En disputa',
  borrowedTag: 'PRESTADO',
  start: 'Entrada',
  end: 'Salida',
  lunch: 'Comida',
  hours: 'Horas',
  addProjectLine: 'Agregar línea de proyecto',
  removeProjectLine: 'Quitar la línea de proyecto',
  noCrewLoaded: 'No se cargaron los integrantes de la cuadrilla. Puede que todavía esté cargando.',
  borrowEmployee: '+ Pedir prestado un empleado',
  removeBorrowedEmployee: 'Quitar al empleado prestado',
  noEmployeesToBorrow: 'No hay empleados disponibles para pedir prestados.',
  searchEmployeesToBorrow: 'Busque empleados para pedir prestados…',

  // horas divididas
  noSplitsYet: 'Todavía no hay empleados con las horas divididas.',
  initializeAllPresent: 'Preparar a todos los presentes',
  initializeAllPresentShort: '👥 Preparar a todos los presentes',
  total: 'Total:',
  addProject: 'Agregar proyecto',
  remaining: 'Faltan:',
  removeSplit: 'Quitar la división',
  applyToOthers: 'Aplicar a otros',
  copySplitsTo: 'Copiar la división a:',
  copySplitsToOthers: 'Copiar la división a otros empleados',
  apply: 'Aplicar',

  // cuadrícula
  gridEntry: 'Captura en cuadrícula',
  noEmployeesPresent: 'No hay empleados presentes — primero agregue empleados al reporte diario',
  noEmployeesToDisplay: 'No hay empleados que mostrar. Primero agregue empleados al reporte diario.',
  employee: 'Empleado',
  classificationShort: 'Clas.',
  totalPlain: 'Total',
  removeProjectColumn: 'Quitar la columna del proyecto',
  fillDown: 'Rellenar hacia abajo — copiar el primer valor a todas las filas',
  lunchAll: 'Comida (todos):',
  startTime: 'Hora de entrada:',
  searchProject: 'Busque un proyecto...',

  // ── recibos ───────────────────────────────────────────────────────────────────────────────────
  receiptsHelp: 'Tome una foto de cada recibo conforme los vaya juntando. Se mandan a la oficina para '
    + 'aprobación cuando usted envía este reporte — no se le carga nada a la obra hasta que Finanzas '
    + 'lo apruebe.',
  uploading: 'Subiendo…',
  retakePhoto: 'Tomar otra foto',
  takePhoto: 'Tomar foto',
  chooseAnother: 'Escoger otra',
  chooseExisting: 'Escoger una que ya tomó',
  takeReceiptPhoto: 'Tomar foto del recibo',
  receiptPhoto: 'Foto del recibo',
  photoRetryHint: (button) => `Toque «${button}» para intentar otra vez.`,
  job: 'Proyecto',
  selectJob: 'Escoja el proyecto…',
  vendor: 'Proveedor',
  whereYouBoughtIt: 'Dónde lo compró',
  category: 'Categoría',
  selectCategory: 'Escoja…',
  notes: 'Notas',
  whatItWasFor: 'Para qué fue (opcional)',
  removeReceipt: 'Quitar el recibo',
  addReceipt: 'Agregar recibo',
  catMaterials: 'Materiales',
  catEquipment: 'Equipo / renta',
  catFuel: 'Combustible',
  catTools: 'Herramienta',
  catSubcontractor: 'Subcontratista',
  catMeals: 'Comidas',
  catTravel: 'Viaje / hospedaje',
  catOther: 'Otro',

  financePending: 'Se mandó a Finanzas — esperando aprobación.',
  financeProcessed: 'Finanzas lo aprobó.',
  financeRejected: 'Finanzas rechazó este recibo.',
  financeRejectedTodo: 'Corríjalo y mande el reporte otra vez, o quite la línea.',
  financeRemoved: 'La oficina quitó este recibo, ya no está en Finanzas.',
  financeRemovedTodo: 'Mande el reporte otra vez para volver a mandarlo, o quite la línea.',
  financeUnknown: (status) => `En Finanzas — ${status}.`,

  // ── descripciones del trabajo ─────────────────────────────────────────────────────────────────
  workPerformed: 'Trabajo realizado',
  describeWork: 'Describa el trabajo que se hizo…',
  addWorkDescription: 'Agregar descripción del trabajo',

  // ── field tickets ─────────────────────────────────────────────────────────────────────────────
  // `Field Tickets` se queda en inglés — ver el encabezado.
  generatedFieldTickets: 'Field Tickets generados',
  hoursLabor: (hours) => `${hours} h de mano de obra`,

  // ── la barra de acciones ──────────────────────────────────────────────────────────────────────
  cancel: 'Cancelar',
  deleteDraft: 'Borrar el borrador',
  receiptStillUploading: 'todavía se está subiendo la foto de un recibo',
  saving: 'Guardando…',
  saveDraft: 'Guardar borrador',
  verify: 'Verificar',
  submitting: 'Enviando…',
  submit: 'Enviar',
  back: 'Atrás',
  requestAmendment: 'Pedir una corrección',
  deleteReport: 'Borrar el reporte',

  // ── borrar un reporte ya enviado ──────────────────────────────────────────────────────────────
  deleteSubmittedTitle: 'Borrar un reporte ya enviado',
  deleteSubmittedBody: (status) => `Este reporte ya está ${status}. Borrarlo es definitivo y no se `
    + 'puede deshacer. Escriba el motivo (mínimo 10 caracteres).',
  deleteReasonPlaceholder: 'Motivo para borrarlo…',
  deleteReasonCount: (n) => `${n}/10 caracteres como mínimo`,
  permanentlyDelete: 'Borrar definitivamente',
  confirmDeleteDraft: '¿Borrar este borrador? No se puede deshacer.',
  confirmAmendment: '¿Crear una corrección? Se hace una copia nueva de este reporte aprobado que sí '
    + 'se puede editar. El original se guarda para auditoría.',

  // ── lo que la pantalla contesta ───────────────────────────────────────────────────────────────
  loadedExistingDraft: 'Se abrió el borrador que ya existía para hoy',
  loadedPrevious: (date) => `Se copiaron los datos del ${date}. Revíselos y ajústelos.`,
  noPreviousReport: 'No se encontró un reporte anterior para copiar.',
  draftSaved: 'Borrador guardado',
  reportSubmitted: 'El reporte se envió para aprobación.',
  amendmentCreated: 'Se creó la corrección. Lo llevamos al borrador nuevo…',
  notReadyToSubmit: 'Todavía no se puede enviar — arregle lo de abajo. El borrador ya quedó guardado.',
  cannotSubmitFixErrors: 'No se puede enviar — arregle los errores de abajo',
  reasonTooShort: 'El motivo debe tener al menos 10 caracteres',
  disputeResolvedReverted: 'Disputa resuelta — cambiaron las horas, el reporte regresó para revisarse otra vez',
  disputeResolvedNotified: 'Disputa resuelta — se le avisó al empleado',
  failedToLoadReport: 'No se pudo cargar el reporte',
  failedToLoadPrevious: 'No se pudo cargar el reporte anterior',
  failedToSave: 'No se pudo guardar',
  failedToDeleteDraft: 'No se pudo borrar el borrador',
  failedToDeleteReport: 'No se pudo borrar el reporte',
  failedToCreateAmendment: 'No se pudo crear la corrección',
  failedToResolveDispute: 'No se pudo resolver la disputa',

  // ── qué terminamos hoy (WorkCompletedStep) ────────────────────────────────────────────────────
  //
  // `instalar` para los precios unitarios (tubería, perforación, conexiones), que es lo que dice la
  // obra; `terminar` para todo lo demás. `señal` y no `conexión`: es lo que dice un supervisor en el
  // campo cuando el teléfono no manda nada.
  wcTitle: 'Qué terminamos hoy',
  wcLoading: 'Cargando lo que espera este proyecto…',
  wcPickJobs: 'Escoja arriba los proyectos donde trabajó su cuadrilla, y aquí aparecerá lo que '
    + 'terminaron en cada uno.',
  wcFromEstimate: 'Del presupuesto — anote lo que terminaron',
  wcFromSchedule: 'De la lista de precios unitarios — anote lo que se instaló',
  wcRequired: 'Su empresa lo pide',
  wcNothingInstalled: 'Hoy no se instaló nada en este proyecto',
  wcFindLine: 'Buscar una línea',
  wcNoLineMatches: 'Ninguna línea coincide.',
  wcOtherLines: 'Otras líneas',
  wcSoFar: (done, estimate, unit) => `${done} de ${estimate} ${unit} reportados hasta ahora`,
  // Avance en partidas largas / asignar al terminar (closed loop P3b).
  wcPctSoFar: (pct) => `${pct}% hecho antes de hoy - indique el % hecho hasta ahora`,
  wcPctOf: (what) => `% hecho hasta ahora de ${what}`,
  wcPctBelow: (pct) => `Menos del ${pct}% ya reportado - un % hecho nunca retrocede.`,
  wcPctAbove: (pct) => `Más del ${pct}% que ya reporta un día posterior - corrija ese día primero.`,
  wcPctOver: 'Una línea no puede estar más del 100% hecha - indique el % hecho hasta ahora, 100 como máximo.',
  wcPctMoveHint: 'Si la cuadrilla trabajó en ella hoy, mueva el % - aunque sea un poco. Un día en que no se mueve, sus horas no se pueden contar para ella.',
  wcInstalledHowMany: (unit) => `Terminada - ¿cuántos ${unit} se instalaron?`,
  wcInstalledOf: (what) => `Cuántos se instalaron de ${what}`,
  wcProgressHint: 'Las líneas que toman más de un día piden el % hecho hasta ahora en lugar de una cantidad.',
  wcHeldHint: 'Su empresa asigna las horas cuando el trabajo termina: marque una partida solo el día en que se termina.',
  wcReplacedElsewhere: (what) => `Su cambio en ${what} reemplazó uno hecho en otro dispositivo.`,
  wcNoLongerListed: 'Ya no está en el presupuesto',
  wcOneLess: (what) => `Uno menos: ${what}`,
  wcOneMore: (what) => `Uno más: ${what}`,
  wcHowManyOf: (what) => `Cuántos: ${what}`,
  wcHowMany: 'Cuántos',
  wcHowManyFinished: 'Cuántos se terminaron',
  wcTypeTheNumber: 'Escriba el número',
  wcAddCount: 'Agregar un conteo',
  wcAddRework: 'Retrabajo: rehacer trabajo terminado',
  wcReworkTitle: 'Retrabajo de hoy',
  wcReworkHint: 'Elija el trabajo terminado que la cuadrilla rehízo. No suma nada terminado; sus horas cuentan como retrabajo.',
  wcReworkPick: 'Qué trabajo terminado se rehízo',
  wcReworkPickOne: 'Elija el trabajo…',
  wcReworkHowMany: 'Cuántos se rehicieron',
  wcReworkWhy: 'Por qué (opcional)',
  wcReworkAdd: 'Agregar el retrabajo',
  wcReworkCancel: 'Cancelar',
  wcReworkNone: 'Aún no hay nada terminado en este trabajo para rehacer.',
  wcReworkLoadFailed: 'No se pudo cargar el trabajo terminado. Intente de nuevo.',
  wcReworkRemove: 'Quitar este retrabajo',
  wcReworkRedoing: (what, n) => `Rehaciendo ${what} × ${n}`,
  wcWeld: 'Soldadura',
  wcRemoveGroup: 'Quitar este grupo',
  wcOptional: '(opcional)',
  wcNotInList: 'No está en la lista…',
  wcSayWhatItIs: 'Escriba qué es',
  wcKeptAsTyped: 'Se guarda tal como lo escribió, para que la oficina lo vea.',
  wcOffHoursTitle: 'Sin horas hoy',
  wcOffHoursBody: (job) => `Este reporte no tiene horas en ${job}, así que esto no se envía. `
    + 'Vuelva a poner las horas o quítelo.',
  wcRemove: 'Quitar',
  wcSaving: 'Guardando…',
  wcSaved: 'Guardado.',
  wcSavedKeptWords: (n) => (n === 1
    ? 'Guardado. 1 palabra queda para que la oficina la vea.'
    : `Guardado. ${n} palabras quedan para que la oficina las vea.`),
  wcNotSentYet: 'Todavía no se envía — queda guardado en este teléfono.',
  wcKeptUntilSaved: 'Todavía no se envía — queda guardado en este teléfono. Se envía cuando guarde '
    + 'el reporte.',
  wcNoSignal: 'Todavía no se envía — queda guardado en este teléfono y se enviará cuando tenga señal.',
  wcRefused: (why) => `El sistema de la oficina no lo aceptó: ${why} Queda guardado en este `
    + 'teléfono — pregunte en la oficina.',
  wcCouldNotLoad: 'No se pudo cargar lo que ya estaba guardado en este reporte. Se carga cuando tenga '
    + 'señal — mientras tanto las líneas no se pueden cambiar, y un conteo que agregue queda en este '
    + 'teléfono.',
  wcLinesWaitForLoad: 'Estas líneas se abren cuando cargue lo que ya estaba guardado, para que nada se '
    + 'cuente dos veces.',
  wcListNotLoaded: 'No se pudo cargar la lista de este proyecto — se cargará cuando tenga señal.',
  wcTryAgain: 'Intentar otra vez',
  wcEnteredEarlier: 'Anotado antes — el resto de la lista aparece cuando cargue',
  wcNotAskedAnyMore: 'Anotado antes de que su empresa dejara de pedirlo',
  wcNotAskedHere: 'Su empresa no pide lo instalado en estos proyectos.',
  wcServerTrouble: 'Todavía no se envía — el sistema de la oficina tuvo un problema. Queda guardado '
    + 'en este teléfono y se intentará otra vez.',
  wcPractice: 'Práctica — aquí no se envía nada.',
  wcDidNotReachOffice: 'Parte de lo que se escribió en este teléfono no llegó a la oficina antes de '
    + 'enviar este reporte.',
  wcLeftOffReport: 'Algunos conteos guardados en este teléfono eran de proyectos sin horas en este '
    + 'reporte, así que no se incluyeron.',
  wcVocab: (id, label) => VOCABULARIO[id] || label || id,
  wcNotReachedOfficeYet: 'Lo que terminaron todavía no llega a la oficina — revise su señal e '
    + 'intente otra vez.',
  // ── el código de costo de las horas y la división por código (ciclo cerrado, fase 3, F1 / F8) ──
  ccLabel: 'Código de costo',
  ccNotSaid: 'Sin indicar',
  ccHint: 'Proyecto de capital. Decir en qué parte del proyecto se trabajaron estas horas permite comparar '
    + 'el presupuesto con lo que el trabajo realmente tomó.',
  ccLastReport: (code) => `Último reporte: ${code}`,
  ccUse: 'Usar',
  csTicked: (off, hours) => `Marcó trabajo en ${off} pero cargó horas solo a ${hours} - este día no se puede `
    + 'dividir por código, así que ninguna de sus horas llegaría al trabajo. Dé a las horas de cada persona su '
    + 'código abajo.',
  csNoCode: 'ningún código',
  csBlockCode: (who, from, to) => `${who} ${from}–${to} código de costo`,
  csPersonCode: (who) => `${who} código de costo`,
  csOnThisJob: (who, hours) => `${who} · ${hours} h en este proyecto`,
  csAnotherCode: '+ horas en otro código',
  csExtraHours: (who, n) => `${who} horas en otro código, línea ${n}`,
  csExtraCode: (who, n) => `${who} otro código de costo, línea ${n}`,
  csHoursOn: 'h en',
  csRemove: 'quitar',
  csSomeone: 'Alguien',
  csGoThere: (job) => `Lo que marcó en ${job} necesita las horas divididas por código - dé a las horas de cada `
    + 'persona su código',
  wcNothingFinished: 'Hoy no se terminó nada en este proyecto',
  wcLastTime: (what) => `La última vez: ${what}`,
  wcUse: 'Usar',
}
